import { useEffect, useMemo, useState } from "react";
import Card from "../components/Card";
import StatCard, { StatDelta } from "../components/StatCard";
import { useRequest } from "../hooks/useRequest";
import {
  getBillingSummary,
  getBillingMonths,
  exportBillingCsv,
  BillingMonth,
} from "../api/billing";
import { getDashboardCredits } from "../api/dashboard";
import { formatCents, formatNumber } from "../utils/format";
import { downloadBlob } from "../utils/download";
import "./Billing.scss";

/** "2026-07" → "Jul 2026" */
function formatMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/** 环比：当月 vs 上月，返回涨跌方向与文案 */
function monthDelta(months: BillingMonth[]): StatDelta | undefined {
  if (months.length < 2) return undefined;
  const current = months[0].total_calls;
  const previous = months[1].total_calls;
  if (!previous) return undefined;
  const pct = ((current - previous) / previous) * 100;
  return {
    direction: pct >= 0 ? "up" : "down",
    text: `${Math.abs(pct).toFixed(1)}% vs last month`,
  };
}

const Billing = () => {
  useEffect(() => {
    document.title = "Billing | CloudStore API";
  }, []);

  const summary = useRequest(getBillingSummary);
  const months = useRequest(getBillingMonths);
  const credits = useRequest(getDashboardCredits);

  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  // 月份按时间倒序，确保 [0] 为最新（当月）
  const monthRows = useMemo(() => {
    const list = months.data ? [...months.data] : [];
    return list.sort((a, b) => b.month.localeCompare(a.month));
  }, [months.data]);

  const delta = useMemo(() => monthDelta(monthRows), [monthRows]);

  const s = summary.data;
  const c = credits.data;

  // 积分卡片：总额度 = planMonthlyCreditsCents；monthlyCreditsCents 是本月「剩余」
  // 所以 已用 = 总额度 - 剩余
  const planTotal = c?.planMonthlyCreditsCents ?? 0;
  const remaining = Math.max(0, Math.min(planTotal, c?.monthlyCreditsCents ?? 0));
  const used = Math.max(0, planTotal - remaining);
  const usedPct = planTotal > 0 ? Math.min(100, (used / planTotal) * 100) : 0;

  const handleExport = async () => {
    setExportErr(null);
    setExporting(true);
    try {
      const blob = await exportBillingCsv();
      downloadBlob(blob, "billing-export.csv");
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="cs-billing">
      <h1 className="cs-billing__title">Billing</h1>

      {/* 三张统计卡片 */}
      <div className="cs-billing__stats">
        <StatCard
          loading={summary.loading}
          variant="primary"
          value={s ? formatCents(s.currentMonthCostCents) : "—"}
          label="Current Month Cost"
        />
        <StatCard
          loading={summary.loading}
          variant="primary"
          value={s ? formatNumber(s.currentMonthCalls) : "—"}
          label="Total API Calls"
          delta={delta}
        />

        {/* 积分使用卡片：已用额度 + 进度条 */}
        <Card>
          <div className="cs-billing__credit-value">
            {c ? formatCents(used) : "—"}
          </div>
          <div className="cs-billing__credit-label">
            Credits Used / {c ? formatCents(planTotal) : "—"}
          </div>
          <div className="cs-billing__credit-bar">
            <div
              className="cs-billing__credit-bar-fill"
              style={{ width: `${usedPct}%` }}
            />
          </div>
          <div className="cs-billing__credit-meta">
            {c
              ? `${Math.round(usedPct)}% used — ${formatCents(
                  remaining
                )} remaining`
              : ""}
          </div>
        </Card>
      </div>

      {/* 月度账单表 */}
      <div className="cs-billing__table-wrap">
        <table className="cs-billing__table">
          <thead>
            <tr>
              <th className="cs-billing__th">Period</th>
              <th className="cs-billing__th">Total Calls</th>
              <th className="cs-billing__th">Total Cost</th>
              <th className="cs-billing__th">Status</th>
            </tr>
          </thead>
          <tbody>
            {months.loading && (
              <tr>
                <td className="cs-billing__state" colSpan={4}>
                  Loading…
                </td>
              </tr>
            )}
            {!months.loading && months.error && (
              <tr>
                <td
                  className="cs-billing__state cs-billing__state--error"
                  colSpan={4}
                >
                  {months.error}
                </td>
              </tr>
            )}
            {!months.loading && !months.error && monthRows.length === 0 && (
              <tr>
                <td className="cs-billing__state" colSpan={4}>
                  No billing records yet.
                </td>
              </tr>
            )}
            {!months.loading &&
              !months.error &&
              monthRows.map((m, i) => {
                // 最新月份视为当月账单（Pending），其余为已结清（Paid）
                const pending = i === 0;
                return (
                  <tr key={m.month} className="cs-billing__row">
                    <td className="cs-billing__td cs-billing__period">
                      {formatMonth(m.month)}
                    </td>
                    <td className="cs-billing__td">
                      {formatNumber(m.total_calls)}
                    </td>
                    <td className="cs-billing__td">
                      {formatCents(m.total_cost_cents)}
                    </td>
                    <td className="cs-billing__td">
                      <span
                        className={`cs-billing__status ${
                          pending
                            ? "cs-billing__status--pending"
                            : "cs-billing__status--paid"
                        }`}
                      >
                        {pending ? "Pending" : "Paid"}
                      </span>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {exportErr && <div className="cs-billing__error">{exportErr}</div>}

      <button
        type="button"
        className="cs-billing__export"
        onClick={handleExport}
        disabled={exporting}
      >
        {exporting ? "Exporting…" : "Export CSV"}
      </button>
    </div>
  );
};

export default Billing;
